// ModifyReservation.jsx
// Route:  /modify-reservation
// APIs:   POST /api/online-bookings/lookup   { ref, email }
//         GET  /api/online-bookings/:id
//         PATCH /api/online-bookings/:id/modify  { ...fields }
//         POST /api/online-bookings/:id/cancel   { reason }
//         GET  /api/locations
//         GET  /api/settings

import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Navbar from "../../components/Navbar";
import { API, BASE_URL } from "../../api";
// const API_BASE = API;
// const API_BASE = "http://localhost:5000/api";
// const API_BASE = "http://192.168.0.57:5000/api";
// Minimum days before pickup that modifications are allowed
const API_BASE = process.env.REACT_APP_API_URL || 'https://carrental.oriesolutions.co.in/api';
const MIN_DAYS_BEFORE = 2;

// ─── CSS ──────────────────────────────────────────────────────
const css = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700;900&family=DM+Sans:wght@300;400;500;600&display=swap');

  :root {
    --gold: #C9A84C; --gold-light: #E8C97A;
    --dark: #0A0A0F; --dark2: #111118; --dark3: #1A1A24;
    --surface: #16161F; --border: rgba(201,168,76,0.18);
    --text: #E8E8F0; --muted: #7A7A9A; --white: #FFFFFF;
    --green: #1D9E75; --red: #E24B4A; --amber: #D97706;
    --nav-h: 68px;
  }

  *, *::before, *::after { margin:0; padding:0; box-sizing:border-box; }
  html { scroll-behavior:smooth; }
  body { font-family:'DM Sans',sans-serif; background:var(--dark); color:var(--text); min-height:100dvh; }

  /* ── NAV ── */
  .mr-nav {
    position:fixed; top:0; left:0; right:0; z-index:1000;
    display:flex; align-items:center; justify-content:space-between;
    padding:0 clamp(1rem,5%,5%); height:var(--nav-h);
    background:rgba(10,10,15,0.97); backdrop-filter:blur(18px);
    border-bottom:1px solid var(--border);
  }
  .mr-logo { font-family:'Playfair Display',serif; font-size:1.55rem; color:var(--gold); cursor:pointer; }
  .mr-logo span { color:var(--white); }
  .mr-nav-links { display:flex; gap:2rem; list-style:none; }
  .mr-nav-links a { color:var(--muted); text-decoration:none; font-size:.875rem; font-weight:500; transition:color .2s; }
  .mr-nav-links a:hover { color:var(--gold); }
  .mr-home-btn {
    background:transparent; color:var(--gold); border:1px solid var(--gold);
    padding:.45rem 1.1rem; border-radius:8px; font-size:.82rem; font-weight:600;
    cursor:pointer; transition:all .2s; font-family:inherit;
  }
  .mr-home-btn:hover { background:var(--gold); color:var(--dark); }

  /* ── PAGE WRAP ── */
  .mr-wrap { padding-top:var(--nav-h); min-height:100dvh; }

  /* ── HERO ── */
  .mr-hero {
    background:var(--dark2); border-bottom:1px solid var(--border);
    padding:clamp(2.5rem,6vw,4rem) clamp(1rem,5%,5%);
    position:relative; overflow:hidden;
  }
  .mr-hero::before {
    content:''; position:absolute; inset:0;
    background:radial-gradient(ellipse 55% 70% at 80% 50%, rgba(201,168,76,.06) 0%, transparent 70%);
    pointer-events:none;
  }
  .mr-hero-inner { position:relative; max-width:700px; }
  .mr-hero-tag { font-size:.68rem; letter-spacing:3px; text-transform:uppercase; color:var(--gold); margin-bottom:.75rem; font-weight:600; }
  .mr-hero-title { font-family:'Playfair Display',serif; font-size:clamp(1.8rem,4.5vw,3rem); color:var(--white); line-height:1.15; margin-bottom:.8rem; }
  .mr-hero-title span { color:var(--gold); }
  .mr-hero-sub { color:var(--muted); font-size:.88rem; line-height:1.7; }
  .mr-hero-sub strong { color:var(--gold); }

  /* ── NOTICE BANNER ── */
  .mr-notice {
    display:flex; align-items:flex-start; gap:.6rem;
    background:rgba(217,119,6,.08); border:1px solid rgba(217,119,6,.28);
    border-radius:10px; padding:.75rem 1rem; font-size:.8rem; color:var(--amber);
    margin-bottom:1.5rem; line-height:1.55;
  }
  .mr-notice .ni { font-size:1rem; flex-shrink:0; margin-top:.05rem; }

  /* ── MAIN CONTENT ── */
  .mr-content { max-width:860px; margin:0 auto; padding:clamp(1.5rem,4vw,3rem) clamp(1rem,4%,5%); }

  /* ── STEP INDICATORS ── */
  .mr-steps {
    display:flex; align-items:center; justify-content:center;
    gap:0; margin-bottom:2.5rem; overflow-x:auto; padding-bottom:.25rem;
  }
  .mr-step { display:flex; flex-direction:column; align-items:center; flex:1; min-width:90px; position:relative; }
  .mr-step:not(:last-child)::after {
    content:''; position:absolute; top:18px;
    left:calc(50% + 18px); right:calc(-50% + 18px);
    height:2px; background:var(--border); z-index:0;
  }
  .mr-step.done:not(:last-child)::after  { background:var(--gold); }
  .mr-step.active:not(:last-child)::after { background:var(--gold); }
  .mr-step-dot {
    width:36px; height:36px; border-radius:50%;
    border:2px solid var(--border); background:var(--surface);
    display:flex; align-items:center; justify-content:center;
    font-size:.8rem; font-weight:700; color:var(--muted);
    position:relative; z-index:1; transition:all .3s;
  }
  .mr-step.done   .mr-step-dot { background:var(--gold); border-color:var(--gold); color:var(--dark); }
  .mr-step.active .mr-step-dot { border-color:var(--gold); color:var(--gold); box-shadow:0 0 0 4px rgba(201,168,76,.15); }
  .mr-step-lbl { font-size:.67rem; color:var(--muted); margin-top:.5rem; text-align:center; font-weight:500; }
  .mr-step.active .mr-step-lbl { color:var(--gold); font-weight:600; }
  .mr-step.done   .mr-step-lbl { color:var(--text); }

  /* ── CARD ── */
  .mr-card {
    background:var(--dark2); border:1px solid var(--border);
    border-radius:18px; padding:clamp(1.2rem,3vw,2rem);
    margin-bottom:1.4rem;
  }
  .mr-card-title {
    font-size:.67rem; letter-spacing:2.5px; text-transform:uppercase;
    color:var(--gold); font-weight:700; margin-bottom:1.3rem;
    display:flex; align-items:center; gap:.6rem;
  }
  .mr-card-title::after { content:''; flex:1; height:1px; background:var(--border); }

  /* ── FORM ELEMENTS ── */
  .mr-grid  { display:grid; grid-template-columns:1fr 1fr; gap:1rem; margin-bottom:1rem; }
  .mr-grid3 { display:grid; grid-template-columns:1fr 1fr 1fr; gap:1rem; margin-bottom:1rem; }
  .mr-grid1 { display:grid; grid-template-columns:1fr; gap:1rem; margin-bottom:1rem; }
  .mr-field { display:flex; flex-direction:column; gap:.38rem; }
  .mr-label { font-size:.67rem; letter-spacing:1.2px; text-transform:uppercase; color:var(--gold); font-weight:600; }
  .mr-input, .mr-select, .mr-textarea {
    background:var(--dark3); border:1px solid var(--border); color:var(--text);
    font-family:'DM Sans',sans-serif; font-size:.875rem;
    padding:.65rem .9rem; border-radius:10px; outline:none;
    transition:border-color .2s; width:100%; -webkit-appearance:none;
  }
  .mr-input:focus, .mr-select:focus, .mr-textarea:focus { border-color:var(--gold); }
  .mr-input::placeholder, .mr-textarea::placeholder { color:var(--muted); }
  .mr-input.err, .mr-select.err { border-color:var(--red); }
  .mr-err { font-size:.7rem; color:var(--red); margin-top:.15rem; }
  .mr-select {
    cursor:pointer; appearance:none;
    background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6'%3E%3Cpath d='M5 6 0 0h10z' fill='%237A7A9A'/%3E%3C/svg%3E");
    background-repeat:no-repeat; background-position:right .9rem center; padding-right:2.2rem;
    background-color:var(--dark3);
  }
  select option { background:#1a1a24; }
  .mr-textarea { resize:vertical; min-height:80px; }
  .mr-dt-pair { display:flex; gap:.5rem; }
  .mr-dt-pair .mr-input  { flex:2; }
  .mr-dt-pair .mr-select { flex:1; }

  /* Operation type pills */
  .mr-op-pills { display:flex; gap:.75rem; margin-bottom:1rem; flex-wrap:wrap; }
  .mr-op-pill {
    flex:1; min-width:140px; display:flex; align-items:center; gap:.6rem;
    background:var(--dark3); border:1.5px solid var(--border);
    border-radius:12px; padding:.85rem 1rem; cursor:pointer; transition:all .2s;
  }
  .mr-op-pill.selected-modify { border-color:var(--gold); background:rgba(201,168,76,.08); }
  .mr-op-pill.selected-cancel { border-color:var(--red);  background:rgba(226,75,74,.08);  }
  .mr-op-pill input[type="radio"] { accent-color:var(--gold); width:16px; height:16px; flex-shrink:0; cursor:pointer; }
  .mr-op-pill.selected-cancel input[type="radio"] { accent-color:var(--red); }
  .mr-op-icon  { font-size:1.2rem; flex-shrink:0; }
  .mr-op-label { font-size:.87rem; font-weight:600; color:var(--text); }
  .mr-op-desc  { font-size:.72rem; color:var(--muted); margin-top:.05rem; }

  /* ── BUTTONS ── */
  .mr-btn-row { display:flex; gap:.8rem; justify-content:flex-end; flex-wrap:wrap; margin-top:1.5rem; }
  .mr-btn-primary {
    background:var(--gold); color:var(--dark); font-weight:700;
    font-family:'DM Sans',sans-serif; font-size:.92rem;
    padding:.75rem 2rem; border:none; border-radius:10px; cursor:pointer;
    transition:all .25s; display:flex; align-items:center; gap:.5rem;
  }
  .mr-btn-primary:hover:not(:disabled) { background:var(--gold-light); transform:translateY(-2px); box-shadow:0 12px 30px rgba(201,168,76,.3); }
  .mr-btn-primary:disabled { opacity:.45; cursor:not-allowed; transform:none; box-shadow:none; }
  .mr-btn-secondary {
    background:transparent; color:var(--muted); border:1px solid var(--border);
    font-family:'DM Sans',sans-serif; font-size:.88rem; font-weight:600;
    padding:.72rem 1.6rem; border-radius:10px; cursor:pointer; transition:all .2s;
  }
  .mr-btn-secondary:hover { border-color:var(--gold); color:var(--gold); }
  .mr-btn-danger {
    background:var(--red); color:var(--white); font-weight:700;
    font-family:'DM Sans',sans-serif; font-size:.92rem;
    padding:.75rem 2rem; border:none; border-radius:10px; cursor:pointer;
    transition:all .25s; display:flex; align-items:center; gap:.5rem;
  }
  .mr-btn-danger:hover:not(:disabled) { background:#c73e3e; transform:translateY(-2px); box-shadow:0 12px 30px rgba(226,75,74,.3); }
  .mr-btn-danger:disabled { opacity:.45; cursor:not-allowed; transform:none; }

  /* ── BOOKING DETAIL ROWS ── */
  .mr-detail-grid { display:grid; grid-template-columns:1fr 1fr; gap:.65rem 1.5rem; margin-bottom:1.2rem; }
  .mr-detail-item { padding:.5rem 0; border-bottom:1px solid var(--border); }
  .mr-detail-item:last-child { border-bottom:none; }
  .mr-detail-lbl { font-size:.67rem; letter-spacing:1px; text-transform:uppercase; color:var(--muted); margin-bottom:.2rem; }
  .mr-detail-val { font-size:.9rem; color:var(--text); font-weight:500; }
  .mr-detail-val.gold { color:var(--gold); font-weight:700; }

  /* Car info bar */
  .mr-car-bar {
    display:flex; align-items:center; gap:1rem;
    background:var(--dark3); border:1px solid var(--border);
    border-radius:12px; padding:.9rem 1.1rem; margin-bottom:1.3rem;
  }
  .mr-car-bar img { width:80px; height:55px; object-fit:cover; border-radius:8px; flex-shrink:0; }
  .mr-car-bar-placeholder { width:80px; height:55px; border-radius:8px; background:var(--surface); display:flex; align-items:center; justify-content:center; font-size:1.8rem; flex-shrink:0; }
  .mr-car-bar-cat  { font-size:.67rem; letter-spacing:1px; text-transform:uppercase; color:var(--gold); font-weight:600; margin-bottom:.15rem; }
  .mr-car-bar-name { font-family:'Playfair Display',serif; font-size:1.05rem; color:var(--white); }
  .mr-car-bar-meta { font-size:.78rem; color:var(--muted); margin-top:.1rem; }

  /* Status badge */
  .mr-status {
    display:inline-flex; align-items:center; gap:.35rem;
    padding:.25rem .7rem; border-radius:20px; font-size:.72rem; font-weight:700; letter-spacing:.5px;
  }
  .mr-status.confirmed { background:rgba(29,158,117,.15); color:var(--green); }
  .mr-status.pending   { background:rgba(217,119,6,.12);  color:var(--amber); }
  .mr-status.cancelled { background:rgba(226,75,74,.12);  color:var(--red);  }

  /* Changed-field highlight */
  .mr-changed { border-color:rgba(201,168,76,.5) !important; background:rgba(201,168,76,.06) !important; }

  /* ── LOADING SPINNER ── */
  .mr-spinner {
    width:42px; height:42px; border:3px solid var(--border);
    border-top-color:var(--gold); border-radius:50%;
    animation:mrspin .75s linear infinite; margin:0 auto;
  }
  @keyframes mrspin { to { transform:rotate(360deg); } }
  .mr-loading { text-align:center; padding:3rem 2rem; }
  .mr-loading p { color:var(--muted); font-size:.85rem; margin-top:1rem; }

  /* ── SUCCESS STATE ── */
  .mr-success { text-align:center; padding:3rem 2rem; }
  .mr-success-icon { font-size:3.5rem; margin-bottom:1rem; }
  .mr-success h2 { font-family:'Playfair Display',serif; font-size:1.8rem; color:var(--white); margin-bottom:.6rem; }
  .mr-success p  { color:var(--muted); font-size:.88rem; line-height:1.7; max-width:420px; margin:0 auto 1.5rem; }
  .mr-success-ref {
    display:inline-block; background:var(--dark3); border:1px solid var(--gold);
    border-radius:10px; padding:.65rem 1.8rem;
    font-family:'Playfair Display',serif; font-size:1.3rem; color:var(--gold); margin-bottom:1.8rem;
  }

  /* ── CANCEL CONFIRM BOX ── */
  .mr-cancel-box {
    background:rgba(226,75,74,.06); border:1px solid rgba(226,75,74,.25);
    border-radius:14px; padding:1.4rem;
  }
  .mr-cancel-box h3 { font-family:'Playfair Display',serif; font-size:1.1rem; color:var(--red); margin-bottom:.6rem; }
  .mr-cancel-box p  { font-size:.83rem; color:var(--muted); line-height:1.65; margin-bottom:1rem; }

  /* ── FULL-SCREEN OVERLAY (during lookup) ── */
  .mr-overlay {
    position:fixed; inset:0; z-index:9999;
    background:rgba(10,10,15,.88); backdrop-filter:blur(6px);
    display:flex; flex-direction:column; align-items:center; justify-content:center; gap:1rem;
  }
  .mr-overlay p { color:var(--muted); font-size:.85rem; letter-spacing:.5px; }

  /* ── RESPONSIVE ── */
  @media (max-width:768px) {
    .mr-nav-links { display:none; }
    .mr-grid, .mr-grid3 { grid-template-columns:1fr; }
    .mr-detail-grid { grid-template-columns:1fr; }
    .mr-btn-row { justify-content:stretch; }
    .mr-btn-row button { flex:1; justify-content:center; }
    .mr-op-pills { flex-direction:column; }
    .mr-op-pill { min-width:unset; }
  }
  @media (max-width:480px) {
    .mr-hero { padding:2rem 1rem; }
    .mr-content { padding:1.2rem 1rem; }
    .mr-card { padding:1rem; }
    .mr-dt-pair { flex-direction:column; }
  }
`;

// ─── CONSTANTS ────────────────────────────────────────────────
const TIME_OPTIONS = Array.from({ length: 24 }, (_, i) =>
  `${String(i).padStart(2, "0")}:00`
);

const today = () => new Date().toISOString().split("T")[0];

// ─── HELPERS ─────────────────────────────────────────────────
const fmtDate = (d) => {
  if (!d) return "—";
  const [y, m, day] = d.split("-");
  const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  return `${day} ${months[Number(m) - 1]} ${y}`;
};

const daysUntil = (dateStr) => {
  if (!dateStr) return 0;
  const pickup = new Date(dateStr);
  pickup.setHours(0, 0, 0, 0);
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  return Math.ceil((pickup - now) / 86400000);
};

const calcDays = (from, to) => {
  if (!from || !to) return 0;
  const diff = (new Date(to) - new Date(from)) / 86400000;
  return diff > 0 ? Math.round(diff) : 0;
};

// const getImg = (img) => {
//   if (!img || img === "" || img === "default.jpg") return null;
//   if (img.startsWith("http")) return img;
//   return `http://localhost:5000/${img}`;
// };
const getImg = (img) => {
  if (!img || img === "" || img === "default.jpg") return null;
  if (img.startsWith("http")) return img;
  return `${BASE_URL}/${img}`;
};

// ─── NAV ─────────────────────────────────────────────────────
// function MrNav({ siteName }) {
//   const navigate = useNavigate();

//   const navLinks = [
//     { label: "Home", path: "/" },
//     { label: "Fleet", path: "/our-cars" },
//     { label: "Modify Reservation", path: "/modify-reservetion" },
//     { label: "About", path: "/about-us" },
//     { label: "Gallery", path: "/gallery" },
//     { label: "Faq", path: "/faqs" },
//     { label: "Contact", path: "/contact" },
//   ];

//   return (
//     <nav className="mr-nav">
//       <div className="mr-logo" onClick={() => navigate("/")}>
//         Orisnz<span>{siteName || "-Fleet"}</span>
//       </div>

//       <ul className="mr-nav-links">
//         {navLinks.map((item) => (
//           <li key={item.label}>
//             <a
//               href={item.path}
//               onClick={(e) => {
//                 e.preventDefault();
//                 navigate(item.path);
//               }}
//             >
//               {item.label}
//             </a>
//           </li>
//         ))}
//       </ul>

//       <button
//         className="mr-home-btn"
//         onClick={() => navigate("/")}
//       >
//         ← Home
//       </button>
//     </nav>
//   );
// }

// ─── STEP 1: LOOKUP FORM ─────────────────────────────────────
function LookupForm({ onFound, onLoading, prefillRef }) {
  const [opType,  setOpType]  = useState("modify");
  const [ref,     setRef]     = useState(prefillRef || "");
  const [email,   setEmail]   = useState("");
  const [errors,  setErrors]  = useState({});
  const [apiErr,  setApiErr]  = useState("");

  const validate = () => {
    const e = {};
    if (!ref.trim())   e.ref   = "Booking reference is required";
    if (!email.trim()) e.email = "Email address is required";
    else if (!/\S+@\S+\.\S+/.test(email)) e.email = "Enter a valid email";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    onLoading(true);
    setApiErr("");

    try {
      // Step A: lookup booking by ref + email
      const res = await fetch(`${API_BASE}/online-bookings/lookup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ref: ref.trim(), email: email.trim() }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || `Booking not found (HTTP ${res.status})`);
      }

      const lookupResult = await res.json();

      // Use whichever ID field the API returns
      const bookingId =
        lookupResult.bookingid    ||
        lookupResult.id           ||
        lookupResult.booking_id   ||
        lookupResult.online_bookingid;

      if (!bookingId) throw new Error("Could not identify booking ID in response.");

      // Step B: fetch full booking details
      const fullRes = await fetch(`${API_BASE}/online-bookings/${bookingId}`);
      if (!fullRes.ok) throw new Error("Could not load full booking details.");
      const fullBooking = await fullRes.json();

      // Pass opType separately so API response can never overwrite it
      onFound(fullBooking, opType);

    } catch (err) {
      setApiErr(err.message || "Could not find your booking. Please check your details.");
    } finally {
      onLoading(false);
    }
  };

  return (
    <div className="mr-card">
      <div className="mr-card-title">Find Your Booking</div>

      <div className="mr-notice">
        <span className="ni">⚠️</span>
        <span>
          You can modify or cancel your reservation at least{" "}
          <strong>{MIN_DAYS_BEFORE} days</strong> before your pickup date.
          Modification generates a new booking reference. Cancellation sends a confirmation email.
        </span>
      </div>

      {/* Operation type selector */}
      <div style={{ marginBottom: "1.2rem" }}>
        <div className="mr-label" style={{ marginBottom: ".6rem" }}>Operation Type</div>
        <div className="mr-op-pills">
          <label className={`mr-op-pill ${opType === "modify" ? "selected-modify" : ""}`}>
            <input
              type="radio" name="optype" value="modify"
              checked={opType === "modify"}
              onChange={() => setOpType("modify")}
            />
            <span className="mr-op-icon">✏️</span>
            <div>
              <div className="mr-op-label">Modify Reservation</div>
              <div className="mr-op-desc">Update dates, times or location</div>
            </div>
          </label>
          <label className={`mr-op-pill ${opType === "cancel" ? "selected-cancel" : ""}`}>
            <input
              type="radio" name="optype" value="cancel"
              checked={opType === "cancel"}
              onChange={() => setOpType("cancel")}
            />
            <span className="mr-op-icon">🚫</span>
            <div>
                <div className="mr-op-label">Cancel Reservation</div>
              <div className="mr-op-desc">Cancel and receive confirmation email</div>
            </div>
          </label>
        </div>
      </div>

      {/* Ref + Email fields */}
      <div className="mr-grid">
        <div className="mr-field">
          <label className="mr-label">Booking Reference Number</label>
          <input
            className={`mr-input${errors.ref ? " err" : ""}`}
            placeholder="e.g. 375"
            value={ref}
            onChange={(e) => { setRef(e.target.value); setErrors(v => ({ ...v, ref: "" })); }}
          />
          {errors.ref && <span className="mr-err">{errors.ref}</span>}
        </div>
        <div className="mr-field">
          <label className="mr-label">Email Address</label>
          <input
            className={`mr-input${errors.email ? " err" : ""}`}
            type="email"
            placeholder="The email used when booking"
            value={email}
            onChange={(e) => { setEmail(e.target.value); setErrors(v => ({ ...v, email: "" })); }}
          />
          {errors.email && <span className="mr-err">{errors.email}</span>}
        </div>
      </div>

      {apiErr && (
        <div style={{
          background: "rgba(226,75,74,.1)", border: "1px solid rgba(226,75,74,.3)",
          borderRadius: "10px", padding: ".75rem 1rem",
          fontSize: ".82rem", color: "var(--red)", marginBottom: "1rem",
          display: "flex", alignItems: "center", gap: ".5rem",
        }}>
          ❌ {apiErr}
        </div>
      )}

      <div className="mr-btn-row">
        <button className="mr-btn-primary" onClick={handleSubmit}>
          Proceed to Next Step →
        </button>
      </div>
    </div>
  );
}

// ─── STEP 2: VIEW + EDIT BOOKING ─────────────────────────────
function BookingEditor({ booking, opType, locations, onSuccess, onBack }) {
  const navigate  = useNavigate();
  const daysLeft  = daysUntil(booking.online_pickdate);
  const canModify = daysLeft >= MIN_DAYS_BEFORE;
  const isModify  = opType === "modify";

  // Editable fields — pre-filled from current booking
  const [pickup,       setPickup]       = useState(booking.online_pickcity    || "");
  const [dropoff,      setDropoff]      = useState(booking.online_returncity  || booking.online_pickcity || "");
  const [diffDrop,     setDiffDrop]     = useState(
    !!(booking.online_returncity && booking.online_returncity !== booking.online_pickcity)
  );
  const [pickupDate,   setPickupDate]   = useState(booking.online_pickdate    || "");
  const [returnDate,   setReturnDate]   = useState(booking.online_returndate  || "");
  const [pickupTime,   setPickupTime]   = useState(booking.online_picktime    || "09:00");
  const [returnTime,   setReturnTime]   = useState(booking.online_returntime  || "09:00");
  const [driverAge,    setDriverAge]    = useState(booking.online_driverage   || "");
  const [cancelReason, setCancelReason] = useState("");
  const [submitting,   setSubmitting]   = useState(false);
  const [errors,       setErrors]       = useState({});

  const [priceInfo,    setPriceInfo]    = useState(null);
  const [priceLoading, setPriceLoading] = useState(false);
  const [apiError,     setApiError]     = useState("");

  const days     = calcDays(pickupDate, returnDate);
  const origDays = calcDays(booking.online_pickdate, booking.online_returndate);

  const bookingId = booking.bookingid
    || booking.online_bookingid
    || booking.booking_id
    || booking.id;

  useEffect(() => {
    const carId = booking.online_carid || booking.car_id;
    if (!carId || days <= 0) { setPriceInfo(null); return; }
    setPriceLoading(true);
    fetch(`${API_BASE}/price/calculate`, {
      method:  "POST",
      headers: { "Content-Type": "application/json" },
      body:    JSON.stringify({ car_id: carId, days }),
    })
      .then(r => r.json())
      .then(d => { setPriceInfo(d); setPriceLoading(false); })
      .catch(() => setPriceLoading(false));
  }, [days, booking.online_carid, booking.car_id]);

  const validateModify = () => {
    const e = {};
    if (!pickup.trim()) e.pickup     = "Pickup location is required";
    if (!pickupDate)    e.pickupDate = "Pickup date is required";
    if (!returnDate)    e.returnDate = "Return date is required";
    if (!driverAge)     e.driverAge  = "Driver age is required";
    if (days <= 0)      e.returnDate = "Return date must be after pickup date";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleModify = async () => {
    if (!validateModify()) return;
    setSubmitting(true);
    setApiError("");
    try {
      if (!bookingId)
        throw new Error(`Booking ID not found. Keys: ${Object.keys(booking).join(", ")}`);

      const payload = {
        online_pickcity:   pickup,
        online_returncity: diffDrop ? dropoff : pickup,
        online_pickdate:   pickupDate,
        online_returndate: returnDate,
        online_picktime:   pickupTime,
        online_returntime: returnTime,
        online_driverage:  driverAge,
        online_diffdate:   days,
      };

      // ── CORRECT endpoint & method ──
      const res = await fetch(`${API_BASE}/online-bookings/${bookingId}/modify`, {
        method:  "PATCH",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify(payload),
      });

      if (!res.ok) {
        let errMsg = `HTTP ${res.status}`;
        try {
          const errData = await res.json();
          errMsg = errData.message || errData.error || JSON.stringify(errData);
        } catch {
          errMsg = await res.text().catch(() => errMsg);
        }
        throw new Error(errMsg);
      }

      await res.json(); // consume response

      // ── Navigate to BookingStep3 with flat state shape matching BookingStep2 ──
      navigate("/booking-step3", {
        state: {
          // ctx fields — flat at root level, matching BookingStep2's handleProceed
          pickup,
          dropoff:     diffDrop ? dropoff : pickup,
          pickupDate,
          returnDate,
          pickupTime,
          returnTime,
          driverAge,
          days,
          diffDrop,

          customer: {
            title: booking.online_title,
            firstName: booking.online_firstname,
            lastName: booking.online_lastname,
            email: booking.online_emailid,
            mobile: booking.online_mobileno,
            address: booking.online_address,
            postcode: booking.online_postcode,
            country: booking.online_country,
            city: booking.online_city,
            dlnumber: booking.dl_number,
            dlexpirydt: booking.dl_expirydate,
            comments: booking.online_comments,

          },

          // selectedCar — mirrors shape CarCard passes to setSelectedCar
          selectedCar: {
            car_id:            booking.online_carid    || booking.car_id,
            car_name:          booking.car_name,
            car_brand:         booking.car_brand        || "",
            car_image:         booking.car_image         || "",
            image_alt:         booking.car_name,
            cat_name:          booking.cat_name          || "",
            group_name:        booking.group_name        || "",
            group_id:          booking.online_groupid    || null,
            car_passgrno:      booking.car_passgrno      || null,
            car_luggno:        booking.car_luggno         || null,
            car_transmission:  booking.car_transmission   || "",
            car_airconditionr: booking.car_airconditionr  || "",
            base_price:
              priceInfo?.final_price  ||
              priceInfo?.base_price   ||
              booking.base_price      ||
              (booking.total_amount
                ? Math.round(Number(booking.total_amount) / Math.max(days, 1))
                : 0),
          },

          selectedExtras:    [],
          selectedInsurance: null,

          // modification metadata
          operationType:      "modify",
          originalBookingRef: booking.booking_ref,
          originalBookingId:  bookingId,
        },
      });
    } catch (err) {
      setApiError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = async () => {
    setSubmitting(true);
    setApiError("");
    try {
      if (!bookingId) throw new Error(`Booking ID not found. Keys: ${Object.keys(booking).join(", ")}`);
      const res = await fetch(`${API_BASE}/online-bookings/cancel/${bookingId}`, {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({ reason: cancelReason }),
      });
      if (!res.ok) {
        let errMsg = `HTTP ${res.status}`;
        try {
          const errData = await res.json();
          errMsg = errData.message || errData.error || JSON.stringify(errData);
        } catch {
          errMsg = await res.text().catch(() => errMsg);
        }
        throw new Error(errMsg);
      }
      onSuccess({
        type:    "cancel",
        newRef:  booking.booking_ref,
        message: "Your reservation has been cancelled. A cancellation confirmation has been sent to your registered email.",
      });
    } catch (err) {
      setApiError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const isChanged = (cur, orig) => String(cur) !== String(orig || "");
  const carImg = getImg(booking.car_image);

  const statusKey = String(booking.status || "pending").toLowerCase();
  const statusClass = statusKey.includes("confirm") ? "confirmed"
    : statusKey.includes("cancel")  ? "cancelled"
    : "pending";

  const locationListId = "mr-location-list";

  return (
    <>
      {locations.length > 0 && (
        <datalist id={locationListId}>
          {locations.map((l) => (
            <option
              key={l.location_id || l.id || l.city_id}
              value={l.location_name || l.city_name || l.name}
            />
          ))}
        </datalist>
      )}

      {/* Current booking summary card */}
      <div className="mr-card">
        <div className="mr-card-title">Current Booking Details</div>

        {/* Car bar */}
        <div className="mr-car-bar">
          {carImg
            ? <img src={carImg} alt={booking.car_name} />
            : <div className="mr-car-bar-placeholder">🚗</div>
          }
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="mr-car-bar-cat">{booking.group_name || booking.cat_name || "Vehicle"}</div>
            <div className="mr-car-bar-name">{booking.car_name || "Your Vehicle"}</div>
            <div className="mr-car-bar-meta">
              {[booking.car_brand, booking.car_transmission, booking.car_passgrno && `${booking.car_passgrno} seats`]
                .filter(Boolean).join(" · ")}
            </div>
          </div>
          <div style={{ textAlign: "right", flexShrink: 0 }}>
            <div className={`mr-status ${statusClass}`}>
              {statusClass === "confirmed" ? "✓ Confirmed"
                : statusClass === "cancelled" ? "✕ Cancelled"
                : "⏳ Pending"}
            </div>
            <div style={{ fontSize: ".7rem", color: "var(--muted)", marginTop: ".4rem" }}>
              Ref: <strong style={{ color: "var(--gold)" }}>{booking.bookingid}</strong>
            </div>
          </div>
        </div>

        {/* Detail grid */}
        <div className="mr-detail-grid">
          {[
            ["Pickup Location",   booking.online_pickcity],
            ["Drop-off Location", booking.online_returncity || booking.online_pickcity],
            ["Pickup Date",       fmtDate(booking.online_pickdate)],
            ["Return Date",       fmtDate(booking.online_returndate)],
            ["Pickup Time",       booking.online_picktime],
            ["Return Time",       booking.online_returntime],
            ["Duration",          `${origDays} day${origDays !== 1 ? "s" : ""}`],
            ["Driver Age",        booking.online_driverage],
            ["Total Amount",      booking.total_amount   ? `₹ ${Number(booking.total_amount).toLocaleString()}`   : "—"],
            ["Advance Paid",      booking.adv_paid ? `₹ ${Number(booking.adv_paid).toLocaleString()}` : "—"],
          ].map(([lbl, val]) => (
            <div key={lbl} className="mr-detail-item">
              <div className="mr-detail-lbl">{lbl}</div>
              <div className="mr-detail-val">{val || "—"}</div>
            </div>
          ))}
        </div>

        {/* Modification lock notice */}
        {!canModify && isModify && (
          <div className="mr-notice">
            <span className="ni">🔒</span>
            <span>
              Your pickup is in <strong>{daysLeft} day{daysLeft !== 1 ? "s" : ""}</strong>.
              Modifications are only allowed at least <strong>{MIN_DAYS_BEFORE} days</strong> before pickup.
            </span>
          </div>
        )}
      </div>

      {/* ── MODIFY FORM ── */}
      {isModify && (
        <div className="mr-card" style={!canModify ? { opacity: .55, pointerEvents: "none" } : {}}>
          <div className="mr-card-title">✏️ Update Booking Details</div>

          {!canModify && (
            <div className="mr-notice" style={{ marginBottom: "1.2rem" }}>
              <span className="ni">🔒</span>
              <span>Modifications are locked — too close to pickup date.</span>
            </div>
          )}

          {/* Pickup location */}
          <div className="mr-grid1">
            <div className="mr-field">
              <label className="mr-label">Pickup Location</label>
              <input
                list={locationListId}
                className={`mr-input${errors.pickup ? " err" : ""}${isChanged(pickup, booking.online_pickcity) ? " mr-changed" : ""}`}
                placeholder="Enter city or airport"
                value={pickup}
                onChange={(e) => { setPickup(e.target.value); setErrors(v => ({ ...v, pickup: "" })); }}
              />
              {errors.pickup && <span className="mr-err">{errors.pickup}</span>}
              <label style={{ display:"flex", alignItems:"center", gap:".4rem", fontSize:".73rem", color:"var(--muted)", marginTop:".4rem", cursor:"pointer" }}>
                <input
                  type="checkbox" checked={diffDrop}
                  onChange={(e) => setDiffDrop(e.target.checked)}
                  style={{ accentColor:"var(--gold)", width:13, height:13 }}
                />
                Drop off at a different location
              </label>
            </div>
          </div>

          {diffDrop && (
            <div className="mr-grid1">
              <div className="mr-field">
                <label className="mr-label">Drop-off Location</label>
                <input
                  list={locationListId}
                  className={`mr-input${isChanged(dropoff, booking.online_returncity) ? " mr-changed" : ""}`}
                  placeholder="Enter drop-off city or airport"
                  value={dropoff}
                  onChange={(e) => setDropoff(e.target.value)}
                />
              </div>
            </div>
          )}

          {/* Dates & times */}
          <div className="mr-grid">
            <div className="mr-field">
              <label className="mr-label">Pickup Date &amp; Time</label>
              <div className="mr-dt-pair">
                <input
                  type="date" min={today()}
                  className={`mr-input${errors.pickupDate ? " err" : ""}${isChanged(pickupDate, booking.online_pickdate) ? " mr-changed" : ""}`}
                  value={pickupDate}
                  onChange={(e) => {
                    setPickupDate(e.target.value);
                    setErrors(v => ({ ...v, pickupDate: "" }));
                    if (!returnDate || returnDate <= e.target.value) {
                      const next = new Date(e.target.value);
                      next.setDate(next.getDate() + 1);
                      setReturnDate(next.toISOString().split("T")[0]);
                    }
                  }}
                />
                <select
                  className={`mr-select${isChanged(pickupTime, booking.online_picktime) ? " mr-changed" : ""}`}
                  value={pickupTime}
                  onChange={(e) => setPickupTime(e.target.value)}
                >
                  {TIME_OPTIONS.map((t) => <option key={t}>{t}</option>)}
                </select>
              </div>
              {errors.pickupDate && <span className="mr-err">{errors.pickupDate}</span>}
            </div>

            <div className="mr-field">
              <label className="mr-label">Return Date &amp; Time</label>
              <div className="mr-dt-pair">
                <input
                  type="date" min={pickupDate || today()}
                  className={`mr-input${errors.returnDate ? " err" : ""}${isChanged(returnDate, booking.online_returndate) ? " mr-changed" : ""}`}
                  value={returnDate}
                  onChange={(e) => { setReturnDate(e.target.value); setErrors(v => ({ ...v, returnDate: "" })); }}
                />
                <select
                  className={`mr-select${isChanged(returnTime, booking.online_returntime) ? " mr-changed" : ""}`}
                  value={returnTime}
                  onChange={(e) => setReturnTime(e.target.value)}
                >
                  {TIME_OPTIONS.map((t) => <option key={t}>{t}</option>)}
                </select>
              </div>
              {errors.returnDate && <span className="mr-err">{errors.returnDate}</span>}
            </div>
          </div>

          {/* Driver age + days badge */}
          <div className="mr-grid">
            <div className="mr-field">
              <label className="mr-label">Driver Age</label>
              <select
                className={`mr-select${errors.driverAge ? " err" : ""}${isChanged(driverAge, booking.online_driverage) ? " mr-changed" : ""}`}
                value={driverAge}
                onChange={(e) => { setDriverAge(e.target.value); setErrors(v => ({ ...v, driverAge: "" })); }}
              >
                <option value="">Select age range</option>
                <option value="21-25">21 – 25 yrs</option>
                <option value="26-35">26 – 35 yrs</option>
                <option value="36-45">36 – 45 yrs</option>
                <option value="46-60">46 – 60 yrs</option>
                <option value="61-70">61 – 70 yrs</option>
                <option value="71+">71+ yrs</option>
              </select>
              {driverAge === "21-25" && (
                <span style={{ fontSize:".7rem", color:"var(--amber)", marginTop:".25rem", display:"block" }}>
                  ⚠️ Young driver surcharge may apply
                </span>
              )}
              {errors.driverAge && <span className="mr-err">{errors.driverAge}</span>}
            </div>

            {days > 0 && (
              <div style={{ display:"flex", alignItems:"flex-end" }}>
                <div style={{
                  background:"rgba(201,168,76,.12)", border:"1px solid var(--gold)",
                  borderRadius:"8px", padding:".65rem 1rem",
                  fontSize:".84rem", color:"var(--gold)", fontWeight:700,
                  whiteSpace:"nowrap", width:"100%", textAlign:"center",
                }}>
                  {days} day{days !== 1 ? "s" : ""}
                  {days !== origDays && (
                    <span style={{ fontSize:".68rem", color:"var(--muted)", display:"block", fontWeight:400 }}>
                      was {origDays} day{origDays !== 1 ? "s" : ""}
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Live price breakdown */}
          {days > 0 && (
            <div style={{
              background: "var(--dark3)", border: "1px solid var(--border)",
              borderRadius: "12px", padding: "1.2rem 1.4rem", marginBottom: "1rem",
            }}>
              <div style={{
                fontSize: ".67rem", letterSpacing: "2px", textTransform: "uppercase",
                color: "var(--gold)", fontWeight: 700, marginBottom: "1rem",
                display: "flex", justifyContent: "space-between", alignItems: "center",
              }}>
                <span>Price Estimate</span>
                {priceLoading && (
                  <span style={{ fontSize: ".72rem", color: "var(--muted)", fontWeight: 400, letterSpacing: 0 }}>
                    Calculating…
                  </span>
                )}
              </div>
              {priceInfo ? (
                <>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: ".83rem", color: "var(--muted)", padding: ".3rem 0", borderBottom: "1px solid var(--border)" }}>
                    <span>Base rate × {days} day{days !== 1 ? "s" : ""}</span>
                    <span style={{ color: "var(--text)" }}>₹ {((priceInfo.base_price || 0) * days).toLocaleString()}</span>
                  </div>
                  {priceInfo.discount > 0 && (
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: ".83rem", color: "var(--muted)", padding: ".3rem 0", borderBottom: "1px solid var(--border)" }}>
                      <span>Duration discount ({priceInfo.discount}%)</span>
                      <span style={{ color: "var(--green)" }}>
                        − ₹ {(((priceInfo.base_price || 0) - (priceInfo.final_price || 0)) * days).toLocaleString()}
                      </span>
                    </div>
                  )}
                  <div style={{ display: "flex", justifyContent: "space-between", paddingTop: ".8rem", marginTop: ".2rem", borderTop: "2px solid var(--gold)" }}>
                    <span style={{ fontWeight: 700, color: "var(--white)", fontSize: ".9rem" }}>Estimated Total</span>
                    <span style={{ fontFamily: "'Playfair Display',serif", fontSize: "1.3rem", color: "var(--gold)", fontWeight: 700 }}>
                      ₹ {((priceInfo.final_price || priceInfo.base_price || 0) * days).toLocaleString()}
                    </span>
                  </div>
                </>
              ) : (
                !priceLoading && (
                  <div style={{ fontSize: ".82rem", color: "var(--muted)" }}>Price not available for this vehicle.</div>
                )
              )}
            </div>
          )}

          {/* API error display */}
          {apiError && (
            <div style={{
              background: "rgba(226,75,74,.1)", border: "1px solid rgba(226,75,74,.35)",
              borderRadius: "10px", padding: "1rem 1.2rem", marginBottom: "1rem",
            }}>
              <div style={{ fontSize: ".78rem", color: "var(--red)", fontWeight: 700, marginBottom: ".4rem" }}>
                ❌ Request Failed
              </div>
              <div style={{ fontSize: ".8rem", color: "var(--red)", wordBreak: "break-all", lineHeight: 1.55 }}>
                {apiError}
              </div>
              <div style={{ marginTop: ".75rem", fontSize: ".72rem", color: "var(--muted)", lineHeight: 1.6 }}>
                <strong style={{ color: "var(--amber)" }}>Debug info:</strong><br />
                Booking ID used: <code style={{ color: "var(--gold)" }}>{String(bookingId)}</code><br />
                Endpoint called: <code style={{ color: "var(--gold)" }}>{API_BASE}/online-bookings/{String(bookingId)}/modify</code><br />
                Available keys: <code style={{ color: "var(--muted)", fontSize: ".68rem" }}>{Object.keys(booking).join(", ")}</code>
              </div>
            </div>
          )}

          <div className="mr-btn-row">
            <button className="mr-btn-secondary" onClick={onBack}>← Back</button>
            <button
              className="mr-btn-primary"
              onClick={handleModify}
              disabled={!canModify || submitting}
            >
              {submitting ? "Updating…" : "Confirm Modification →"}
            </button>
          </div>
        </div>
      )}

      {/* ── CANCEL PANEL ── */}
      {!isModify && (
        <div className="mr-card">
          <div className="mr-card-title">🚫 Cancel Reservation</div>
          <div className="mr-cancel-box">
            <h3>Are you sure you want to cancel?</h3>
            <p>
              This action cannot be undone. A cancellation confirmation will be sent to your
              registered email. Any advance amount paid may be subject to the cancellation policy.
              {daysLeft < MIN_DAYS_BEFORE && daysLeft > 0 && (
                <> Your pickup is in <strong style={{ color:"var(--red)" }}>{daysLeft} day{daysLeft !== 1 ? "s" : ""}</strong> — late cancellation charges may apply.</>
              )}
            </p>
            <div className="mr-field">
              <label className="mr-label">Reason for Cancellation (optional)</label>
              <textarea
                className="mr-textarea"
                placeholder="Let us know why you're cancelling…"
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
              />
            </div>
          </div>
          {apiError && (
            <div style={{
              background: "rgba(226,75,74,.1)", border: "1px solid rgba(226,75,74,.35)",
              borderRadius: "10px", padding: "1rem 1.2rem", marginTop: "1rem",
              fontSize: ".8rem", color: "var(--red)", wordBreak: "break-all", lineHeight: 1.55,
            }}>
              ❌ {apiError}
            </div>
          )}
          <div className="mr-btn-row">
            <button className="mr-btn-secondary" onClick={onBack}>← Keep My Booking</button>
            <button
              className="mr-btn-danger"
              onClick={handleCancel}
              disabled={submitting}
            >
              {submitting ? "Cancelling…" : "Yes, Cancel Reservation"}
            </button>
          </div>
        </div>
      )}
    </>
  );
}

// ─── STEP 3: SUCCESS / RESULT ─────────────────────────────────
function SuccessView({ result, onReset }) {
  const navigate  = useNavigate();
  const isModify  = result.type === "modify";
  return (
    <div className="mr-card">
      <div className="mr-success">
        <div className="mr-success-icon">{isModify ? "✅" : "🚫"}</div>
        <h2>{isModify ? "Booking Modified!" : "Booking Cancelled"}</h2>
        <p>{result.message}</p>
        {isModify && result.newRef && (
          <div className="mr-success-ref">{result.newRef}</div>
        )}
        <div style={{ display:"flex", gap:"1rem", justifyContent:"center", flexWrap:"wrap" }}>
          <button className="mr-btn-primary" onClick={() => navigate("/")}>
            Back to Home
          </button>
          <button className="mr-btn-secondary" onClick={onReset}>
            {isModify ? "Modify Another Booking" : "Look Up Another Booking"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── ROOT PAGE COMPONENT ──────────────────────────────────────
export default function ModifyReservation() {
  const navigate  = useNavigate();
  const location  = useLocation();

  const urlParams  = new URLSearchParams(location.search);
  const prefillRef = urlParams.get("ref") || "";

  const [step,      setStep]     = useState(1);
  const [booking,   setBooking]  = useState(null);
  const [opType,    setOpType]   = useState("modify");
  const [result,    setResult]   = useState(null);
  const [loading,   setLoading]  = useState(false);
  const [locations, setLocations]= useState([]);
  const [settings,  setSettings] = useState({ site_name: "-Fleet" });

  // useEffect(() => {
  //   fetch(`${API_BASE}/settings`)
  //     .then(r => r.json()).then(d => { if (d) setSettings(d); }).catch(() => {});
  //   fetch(`${API_BASE}/locations`)
  //     .then(r => r.json()).then(d => {
  //       const arr = Array.isArray(d) ? d : Array.isArray(d?.data) ? d.data : [];
  //       setLocations(arr);
  //     }).catch(() => {});
  // }, []);

  useEffect(() => {
    API.get('/settings')
      .then(r => { if (r.data) setSettings(r.data); }).catch(() => {});
    API.get('/locations')
      .then(r => {
        const arr = Array.isArray(r.data) ? r.data : Array.isArray(r.data?.data) ? r.data.data : [];
        setLocations(arr);
      }).catch(() => {});
  }, []);

  const STEP_LABELS = ["Find Booking", "Review & Edit", "Confirmation"];

  const handleFound = (b, op) => {
    setBooking(b);
    setOpType(op);
    setStep(2);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSuccess = (r) => {
    setResult(r);
    setStep(3);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleReset = () => {
    setStep(1);
    setBooking(null);
    setOpType("modify");
    setResult(null);
  };

  return (
    <>
      <style>{css}</style>

      {loading && (
        <div className="mr-overlay">
          <div className="mr-spinner" />
          <p>Looking up your booking…</p>
        </div>
      )}

      {/* <MrNav siteName={settings?.site_name} /> */}
      <Navbar activePage="Modify Reservation" siteName={settings?.site_name} />

      <div className="mr-wrap">
        <div className="mr-hero">
          <div className="mr-hero-inner">
            <div className="mr-hero-tag">Booking Management</div>
            <h1 className="mr-hero-title">Modify <span>Reservation</span></h1>
            <p className="mr-hero-sub">
              Update your pickup dates, times or location — or cancel your reservation.
              Changes must be made at least{" "}
              <strong>{MIN_DAYS_BEFORE} days</strong> before pickup.
            </p>
          </div>
        </div>

        <div className="mr-content">
          {/* Step indicators */}
          <div className="mr-steps">
            {STEP_LABELS.map((lbl, i) => {
              const n     = i + 1;
              const state = n < step ? "done" : n === step ? "active" : "";
              return (
                <div key={lbl} className={`mr-step ${state}`}>
                  <div className="mr-step-dot">{n < step ? "✓" : n}</div>
                  <div className="mr-step-lbl">{lbl}</div>
                </div>
              );
            })}
          </div>

          {step === 1 && (
            <LookupForm
              prefillRef={prefillRef}
              onFound={handleFound}
              onLoading={setLoading}
            />
          )}

          {step === 2 && booking && (
            <BookingEditor
              booking={booking}
              opType={opType}
              locations={locations}
              onSuccess={handleSuccess}
              onBack={handleReset}
            />
          )}

          {step === 3 && result && (
            <SuccessView result={result} onReset={handleReset} />
          )}
        </div>
      </div>
    </>
  );
}